/**
 * usePlans hooks (Spec 201 — FR-033..035)
 *
 * React Query hooks for Backup Plan management (list/detail/create/update/lifecycle/
 * legal-hold/targets). Mirrors the existing module hook conventions.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { plansApi, BackupPlan, BackupPlanDetail, CreatePlanInput, BackupEntityType } from '../api/plans';

const KEY = ['backup', 'plans'] as const;

export function usePlans() {
  return useQuery<BackupPlan[]>({ queryKey: KEY, queryFn: plansApi.list });
}

export function usePlan(id: string | undefined) {
  return useQuery<BackupPlanDetail>({
    queryKey: [...KEY, id],
    queryFn: () => plansApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePlanInput) => plansApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePlan(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreatePlanInput>) => plansApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePlanLifecycle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: 'pause' | 'activate' | 'archive') => plansApi.transition(id, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePlanLegalHold(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hold: boolean) => plansApi.setLegalHold(id, hold),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function usePlanTargets(id: string) {
  const qc = useQueryClient();
  const attach = useMutation({
    mutationFn: (t: { entityType: BackupEntityType; entityId: string }) =>
      plansApi.attachTarget(id, t.entityType, t.entityId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, id] }),
  });
  const detach = useMutation({
    mutationFn: (t: { entityType: BackupEntityType; entityId: string }) =>
      plansApi.detachTarget(id, t.entityType, t.entityId),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, id] }),
  });
  return { attach, detach };
}
